import React,{createContext,useContext,useMemo,useState} from "react";
import { ThemeProvider as MuiThemeProvider,createTheme,CssBaseline } from "@mui/material";

const ThemeContext=createContext(null);

const lightColors={
  primary:"#0B1220",
  primarySoft:"#162033",

  secondary:"#1D4ED8",
  secondaryHover:"#1E40AF",

  accent:"#1D4ED8",
  accentHover:"#1E40AF",

  focus:"#164E63",
  focusHover:"#155E75",

  background:"#F8FAFC",
  backgroundSoft:"#F1F5F9",
  surface:"#FFFFFF",
  surfaceAlt:"#F8FAFC",

  text:"#0F172A",
  textSecondary:"#475569",
  textMuted:"#94A3B8",

  border:"#E2E8F0",
  borderDark:"#CBD5E1",

  inputBackground:"#FFFFFF",
  inputText:"#0F172A",
  inputPlaceholder:"#94A3B8",

  tableHeader:"#1D4ED8",
  tableHeaderText:"#FFFFFF",

  buttonPrimary:"#1D4ED8",
  buttonPrimaryHover:"#1E40AF",
  buttonPrimaryText:"#FFFFFF",

  buttonOutlinedBorder:"#CBD5E1",
  buttonOutlinedText:"#0F172A",
  buttonOutlinedHoverBorder:"#1D4ED8",
  buttonOutlinedHoverText:"#1D4ED8",
  buttonOutlinedHoverBackground:"#F1F5F9",

  buttonText:"#475569",
  buttonTextHover:"#1D4ED8",
  buttonTextHoverBackground:"#F1F5F9",

  controlActive:"#1D4ED8",
  controlInactive:"#94A3B8",

  success:"#15803D",
  warning:"#B45309",
  error:"#B91C1C",
  info:"#1D4ED8",
};

const darkColors={
  primary:"#080D16",
  primarySoft:"#111827",

  secondary:"#3B82F6",
  secondaryHover:"#2563EB",

  accent:"#3B82F6",
  accentHover:"#2563EB",

  focus:"#164E63",
  focusHover:"#155E75",

  background:"#080D16",
  backgroundSoft:"#0D1420",
  surface:"#111827",
  surfaceAlt:"#182233",

  text:"#FFFFFF",
  textSecondary:"#CBD5E1",
  textMuted:"#94A3B8",

  border:"#293548",
  borderDark:"#475569",

  inputBackground:"#111827",
  inputText:"#FFFFFF",
  inputPlaceholder:"#94A3B8",

  tableHeader:"#3B82F6",
  tableHeaderText:"#FFFFFF",

  buttonPrimary:"#3B82F6",
  buttonPrimaryHover:"#2563EB",
  buttonPrimaryText:"#FFFFFF",

  buttonOutlinedBorder:"#475569",
  buttonOutlinedText:"#FFFFFF",
  buttonOutlinedHoverBorder:"#3B82F6",
  buttonOutlinedHoverText:"#FFFFFF",
  buttonOutlinedHoverBackground:"#182233",

  buttonText:"#CBD5E1",
  buttonTextHover:"#FFFFFF",
  buttonTextHoverBackground:"#182233",

  controlActive:"#3B82F6",
  controlInactive:"#64748B",

  success:"#22C55E",
  warning:"#F59E0B",
  error:"#EF4444",
  info:"#60A5FA",
};

const typography={
  fontFamily:'"Inter","Roboto","Helvetica","Arial",sans-serif',
  sizes:{
    xs:"0.72rem",
    sm:"0.8rem",
    md:"0.88rem",
    base:"0.94rem",
    lg:"1rem",
    xl:"1.25rem",
    title:"1.5rem",
    pageTitle:"1.8rem",
  },
  weights:{
    regular:400,
    medium:500,
    semibold:600,
    bold:700,
    extraBold:800,
  },
};

const shape={
  radiusSmall:"8px",
  radius:"10px",
  radiusLarge:"14px",
};

export function AppThemeProvider({children}){
  const [mode,setMode]=useState(()=>{
    const stored=localStorage.getItem("appThemeMode");
    return stored==="dark"?"dark":"light";
  });

  const colors=mode==="dark"?darkColors:lightColors;

  const setThemeMode=(newMode)=>{
    const nextMode=newMode==="dark"?"dark":"light";
    setMode(nextMode);
    localStorage.setItem("appThemeMode",nextMode);
  };

  const toggleTheme=()=>{
    setMode((current)=>{
      const next=current==="light"?"dark":"light";
      localStorage.setItem("appThemeMode",next);
      return next;
    });
  };

  const muiTheme=useMemo(()=>createTheme({
    palette:{
      mode,
      primary:{
        main:colors.secondary,
        contrastText:colors.tableHeaderText,
      },
      secondary:{
        main:colors.secondary,
        contrastText:colors.tableHeaderText,
      },
      background:{
        default:colors.background,
        paper:colors.surface,
      },
      text:{
        primary:colors.text,
        secondary:colors.textSecondary,
      },
      divider:colors.border,
      success:{main:colors.success},
      warning:{main:colors.warning},
      error:{main:colors.error},
      info:{main:colors.info},
    },

    typography:{
      fontFamily:typography.fontFamily,
      fontWeightRegular:typography.weights.regular,
      fontWeightMedium:typography.weights.medium,
      fontWeightBold:typography.weights.bold,

      h1:{
        fontSize:typography.sizes.pageTitle,
        fontWeight:typography.weights.extraBold,
      },

      h2:{
        fontSize:typography.sizes.title,
        fontWeight:typography.weights.extraBold,
      },

      h3:{
        fontSize:typography.sizes.xl,
        fontWeight:typography.weights.bold,
      },

      body1:{
        fontSize:typography.sizes.base,
        fontWeight:typography.weights.regular,
      },

      body2:{
        fontSize:typography.sizes.md,
        fontWeight:typography.weights.regular,
      },

      button:{
        fontSize:typography.sizes.md,
        fontWeight:typography.weights.bold,
        textTransform:"none",
      },
    },

    shape:{
      borderRadius:10,
    },

    components:{
      MuiCssBaseline:{
        styleOverrides:{
          body:{
            backgroundColor:colors.background,
            color:colors.text,
            transition:"background-color .2s ease,color .2s ease",
          },

          "*":{
            boxSizing:"border-box",
          },

          "input,textarea":{
            color:colors.inputText,
            WebkitTextFillColor:colors.inputText,
            caretColor:colors.inputText,
          },

          "input::placeholder,textarea::placeholder":{
            color:colors.inputPlaceholder,
            WebkitTextFillColor:colors.inputPlaceholder,
            opacity:1,
          },

          "input:-webkit-autofill,input:-webkit-autofill:hover,input:-webkit-autofill:focus,textarea:-webkit-autofill,select:-webkit-autofill":{
            WebkitTextFillColor:colors.inputText,
            caretColor:colors.inputText,
            WebkitBoxShadow:`0 0 0 1000px ${colors.inputBackground} inset`,
            transition:"background-color 9999s ease-in-out 0s",
          },

          "*::-webkit-scrollbar":{
            width:"7px",
            height:"7px",
          },

          "*::-webkit-scrollbar-track":{
            background:"transparent",
          },

          "*::-webkit-scrollbar-thumb":{
            backgroundColor:colors.borderDark,
            borderRadius:"10px",
          },
        },
      },

      MuiButton:{
        defaultProps:{
          size:"small",
          disableElevation:true,
        },

        styleOverrides:{
          root:{
            minHeight:36,
            borderRadius:shape.radiusSmall,
            textTransform:"none",
            fontSize:typography.sizes.md,
            fontWeight:typography.weights.bold,
            boxShadow:"none",

            "&:hover":{
              boxShadow:"none",
            },
          },

          contained:{
            backgroundColor:colors.buttonPrimary,
            color:colors.buttonPrimaryText,

            "&:hover":{
              backgroundColor:colors.buttonPrimaryHover,
            },
          },

          containedPrimary:{
            backgroundColor:colors.buttonPrimary,
            color:colors.buttonPrimaryText,

            "&:hover":{
              backgroundColor:colors.buttonPrimaryHover,
            },
          },

          containedSecondary:{
            backgroundColor:colors.buttonPrimary,
            color:colors.buttonPrimaryText,

            "&:hover":{
              backgroundColor:colors.buttonPrimaryHover,
            },
          },

          outlined:{
            borderColor:colors.buttonOutlinedBorder,
            color:colors.buttonOutlinedText,

            "&:hover":{
              borderColor:colors.buttonOutlinedHoverBorder,
              color:colors.buttonOutlinedHoverText,
              backgroundColor:colors.buttonOutlinedHoverBackground,
            },
          },

          text:{
            color:colors.buttonText,

            "&:hover":{
              color:colors.buttonTextHover,
              backgroundColor:colors.buttonTextHoverBackground,
            },
          },
        },
      },

      MuiIconButton:{
        defaultProps:{
          size:"small",
        },

        styleOverrides:{
          root:{
            borderRadius:shape.radiusSmall,
            color:colors.textSecondary,

            "&:hover":{
              color:colors.text,
              backgroundColor:colors.backgroundSoft,
            },
          },
        },
      },

      MuiCheckbox:{
        defaultProps:{
          size:"small",
        },

        styleOverrides:{
          root:{
            color:colors.controlInactive,

            "&.Mui-checked":{
              color:colors.controlActive,
            },

            "&.MuiCheckbox-indeterminate":{
              color:colors.controlActive,
            },
          },
        },
      },

      MuiRadio:{
        defaultProps:{
          size:"small",
        },

        styleOverrides:{
          root:{
            color:colors.controlInactive,

            "&.Mui-checked":{
              color:colors.controlActive,
            },
          },
        },
      },

      MuiSwitch:{
        defaultProps:{
          size:"small",
        },

        styleOverrides:{
          switchBase:{
            color:colors.controlInactive,

            "&.Mui-checked":{
              color:colors.controlActive,

              "& + .MuiSwitch-track":{
                backgroundColor:colors.controlActive,
                opacity:0.5,
              },
            },
          },

          track:{
            backgroundColor:colors.controlInactive,
            opacity:0.45,
          },
        },
      },

      MuiCircularProgress:{
        styleOverrides:{
          root:{
            color:colors.secondary,
          },
        },
      },

      MuiTextField:{
        defaultProps:{
          size:"small",
        },
      },

      MuiFormControl:{
        defaultProps:{
          size:"small",
        },
      },

      MuiInputBase:{
        styleOverrides:{
          root:{
            color:colors.inputText,
          },

          input:{
            color:colors.inputText,
            WebkitTextFillColor:colors.inputText,
            caretColor:colors.inputText,

            "&::placeholder":{
              color:colors.inputPlaceholder,
              WebkitTextFillColor:colors.inputPlaceholder,
              opacity:1,
            },

            "&:-webkit-autofill":{
              WebkitTextFillColor:colors.inputText,
              caretColor:colors.inputText,
              WebkitBoxShadow:`0 0 0 1000px ${colors.inputBackground} inset`,
            },
          },
        },
      },

      MuiOutlinedInput:{
        styleOverrides:{
          root:{
            borderRadius:shape.radiusSmall,
            backgroundColor:colors.inputBackground,
            color:colors.inputText,

            "& .MuiOutlinedInput-notchedOutline":{
              borderColor:colors.border,
            },

            "&:hover .MuiOutlinedInput-notchedOutline":{
              borderColor:colors.borderDark,
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline":{
              borderColor:colors.focus,
              borderWidth:"1px",
            },

            "&.Mui-error .MuiOutlinedInput-notchedOutline":{
              borderColor:colors.error,
            },

            "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline":{
              borderColor:colors.error,
            },

            "&.Mui-disabled":{
              backgroundColor:colors.backgroundSoft,
            },

            "&.Mui-disabled .MuiOutlinedInput-input":{
              WebkitTextFillColor:colors.textMuted,
            },
          },

          input:{
            color:colors.inputText,
            WebkitTextFillColor:colors.inputText,
            caretColor:colors.inputText,

            "&::placeholder":{
              color:colors.inputPlaceholder,
              WebkitTextFillColor:colors.inputPlaceholder,
              opacity:1,
            },

            "&:-webkit-autofill":{
              WebkitTextFillColor:colors.inputText,
              caretColor:colors.inputText,
              WebkitBoxShadow:`0 0 0 1000px ${colors.inputBackground} inset`,
            },
          },
        },
      },

      MuiInputLabel:{
        styleOverrides:{
          root:{
            fontSize:typography.sizes.md,
            color:colors.textSecondary,

            "&.Mui-focused":{
              color:colors.focus,
            },

            "&.Mui-error":{
              color:colors.error,
            },

            "&.Mui-error.Mui-focused":{
              color:colors.error,
            },

            "&.Mui-disabled":{
              color:colors.textMuted,
            },
          },
        },
      },

      MuiFormLabel:{
        styleOverrides:{
          root:{
            fontSize:typography.sizes.md,
            color:colors.textSecondary,

            "&.Mui-focused":{
              color:colors.focus,
            },

            "&.Mui-error":{
              color:colors.error,
            },
          },
        },
      },

      MuiFormControlLabel:{
        styleOverrides:{
          label:{
            fontSize:typography.sizes.md,
            color:colors.text,
          },
        },
      },

      MuiFormHelperText:{
        styleOverrides:{
          root:{
            fontSize:typography.sizes.xs,

            "&.Mui-error":{
              color:colors.error,
            },
          },
        },
      },

      MuiSelect:{
        defaultProps:{
          MenuProps:{
            PaperProps:{
              sx:{
                maxHeight:320,
                overflowY:"auto",
              },
            },
          },
        },

        styleOverrides:{
          select:{
            color:mode==="dark"?"#FFFFFF":colors.inputText,
            WebkitTextFillColor:mode==="dark"?"#FFFFFF":colors.inputText,
          },

          icon:{
            color:mode==="dark"?"#FFFFFF":colors.textSecondary,
          },
        },
      },

      MuiMenu:{
        defaultProps:{
          MenuListProps:{
            sx:{
              maxHeight:320,
              overflowY:"auto",
            },
          },
        },
      },

      MuiMenuItem:{
        styleOverrides:{
          root:{
            minHeight:38,
            fontSize:typography.sizes.md,
            fontWeight:typography.weights.regular,
            color:mode==="dark"?"#FFFFFF":colors.text,

            "&:hover":{
              backgroundColor:colors.backgroundSoft,
            },

            "&.Mui-selected":{
              backgroundColor:colors.surfaceAlt,
              color:mode==="dark"?"#FFFFFF":colors.text,
            },

            "&.Mui-selected:hover":{
              backgroundColor:colors.surfaceAlt,
              color:mode==="dark"?"#FFFFFF":colors.text,
            },
          },
        },
      },

      MuiTableContainer:{
        styleOverrides:{
          root:{
            overflowX:"auto",
          },
        },
      },

      MuiTableHead:{
        styleOverrides:{
          root:{
            backgroundColor:colors.tableHeader,

            "& .MuiTableCell-root":{
              backgroundColor:colors.tableHeader,
              color:colors.tableHeaderText,
              fontSize:typography.sizes.sm,
              fontWeight:typography.weights.bold,
              borderBottom:`1px solid ${colors.tableHeader}`,
              whiteSpace:"nowrap",
            },
          },
        },
      },

      MuiTableCell:{
        styleOverrides:{
          root:{
            borderBottom:`1px solid ${colors.border}`,
            color:colors.text,
            fontSize:typography.sizes.md,
          },
        },
      },

      MuiTableRow:{
        styleOverrides:{
          root:{
            "&:hover":{
              backgroundColor:colors.backgroundSoft,
            },
          },
        },
      },

      MuiTablePagination:{
        styleOverrides:{
          root:{
            color:colors.textSecondary,
            borderTop:`1px solid ${colors.border}`,
          },

          selectLabel:{
            color:colors.textSecondary,
          },

          displayedRows:{
            color:colors.textSecondary,
          },

          select:{
            color:mode==="dark"?"#FFFFFF":colors.text,
            WebkitTextFillColor:mode==="dark"?"#FFFFFF":colors.text,
          },

          selectIcon:{
            color:mode==="dark"?"#FFFFFF":colors.textSecondary,
          },

          actions:{
            color:colors.text,
          },
        },
      },

      MuiPaper:{
        styleOverrides:{
          root:{
            backgroundImage:"none",
            backgroundColor:colors.surface,
            color:colors.text,
          },
        },
      },

      MuiDialog:{
        styleOverrides:{
          paper:{
            borderRadius:shape.radiusLarge,
            boxShadow:"none",
            border:`1px solid ${colors.border}`,
            backgroundColor:colors.surface,
          },
        },
      },

      MuiDialogTitle:{
        styleOverrides:{
          root:{
            fontWeight:typography.weights.extraBold,
            color:colors.text,
          },
        },
      },

      MuiDialogContent:{
        styleOverrides:{
          root:{
            color:colors.text,
          },
        },
      },

      MuiDialogActions:{
        styleOverrides:{
          root:{
            borderTop:`1px solid ${colors.border}`,
          },
        },
      },

      MuiTooltip:{
        styleOverrides:{
          tooltip:{
            fontSize:typography.sizes.xs,
          },
        },
      },
    },
  }),[mode,colors]);

  const value=useMemo(()=>({
    mode,
    isDark:mode==="dark",
    colors,
    typography,
    shape,
    setThemeMode,
    toggleTheme,
  }),[mode,colors]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useAppTheme(){
  const context=useContext(ThemeContext);
  if(!context) throw new Error("useAppTheme debe usarse dentro de AppThemeProvider");
  return context;
}